import React, { useState } from "react";
import { CaretDown, CaretUp, Crosshair } from "@phosphor-icons/react";

// Sprint 64.5 — "what actually fired" panel. Reads the request config the
// backend used for the LAST build (after derive_config overlaid the routed
// preset / anchor blend / slate gate) so audits never have to guess.

const fmtBands = (bands) => (bands || []).map((b) => `${Math.round(b.weight * 100)}% $${(b.floor / 1000).toFixed(1)}k–${(b.cap / 1000).toFixed(1)}k`).join(" · ");

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3 text-[11px] mono py-0.5" data-testid={testId}>
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-200 text-right">{value ?? "—"}</span>
  </div>
);

// Grade a finished build against the rules that produced it.
export function gradeBuild(lineups, config) {
  const L = Array.isArray(lineups) ? lineups : [];
  if (!L.length || !config) return null;
  const n = L.length;
  const qbTeam = (x) => (x.players || []).find((p) => p.position === "QB")?.team;
  const naked = L.filter((x) => { const t = qbTeam(x); return t && !(x.players || []).some((p) => ["WR", "TE"].includes(p.position) && p.team === t); }).length;
  const relaxed = L.filter((x) => /(_relaxed|_fallback)$/.test(String(x.stack_type || "")) || x.tertiary_pin_relaxed).length;
  const cap = Number(config.salary_cap || 50000);
  const bands = config.salary_jitter_bands || [];
  const inBand = bands.length ? L.filter((x) => bands.some((b) => x.salary >= b.floor && x.salary <= b.cap)).length : null;
  const overCap = L.filter((x) => x.salary > cap).length;
  const ownOut = L.filter((x) => x.total_own != null && (x.total_own < Number(config.sum_own_min) - 0.5 || x.total_own > Number(config.sum_own_max) + 0.5)).length;
  const counts = {};
  L.forEach((x) => (x.players || []).forEach((p) => { counts[p.dk_id] = (counts[p.dk_id] || 0) + 1; }));
  const worst = Math.max(0, ...Object.values(counts));
  const capPct = Number(config.max_exposure_pct || 100);
  const expBreach = Object.values(counts).filter((c) => c > Math.max(1, Math.floor((n * capPct) / 100))).length;
  const mu = Number(config.min_unique_players || 0);
  let pairViol = 0;
  if (mu > 0) {
    const sets = L.map((x) => new Set((x.players || []).map((p) => p.dk_id)));
    const size = (sets[0] && sets[0].size) || 9;
    for (let i = 0; i < sets.length; i++) for (let j = i + 1; j < sets.length; j++) {
      let shared = 0; sets[i].forEach((id) => { if (sets[j].has(id)) shared += 1; });
      if (shared > size - mu) pairViol += 1;
    }
  }
  return { n, naked, relaxed, inBand, bandsLen: bands.length, overCap, ownOut, worst, worstPct: Math.round((100 * worst) / n), expBreach, pairViol, mu };
}

const Grade = ({ ok, label, value, testId }) => (
  <div className="flex justify-between gap-3 text-[11px] mono py-0.5" data-testid={testId}>
    <span className="text-zinc-500">{label}</span>
    <span className={ok ? "text-[#39FF14]" : "text-[#FF3B30] font-bold"}>{value}</span>
  </div>
);

export default function ActiveRuleset({ config, showdownRules, lineups, preview = false }) {
  const [open, setOpen] = useState(true);
  if (!config) return null;
  const meta = config.ruleset_meta || null;
  const g = showdownRules || preview ? null : gradeBuild(lineups, config);
  const sc = meta?.scaling || null;
  const routeLabel = showdownRules
    ? `Showdown · ${showdownRules.showdown_tier || config.showdown_tier || "captain mode"}`
    : sc?.mode === "slate_gate"
      ? `Partial Slate (${sc.gate === "short_slate" ? "≤4-game slate" : "5–6 games, ≥50 entries"})`
      : sc?.blend_label
        ? `${meta.label || meta.preset} · blend ${sc.blend_label}`
        : meta?.label || meta?.preset || config.contest_type || "Request rules";
  const pct = (v) => (v == null ? "—" : `${Math.round(Number(v) * 100)}%`);
  return (
    <div className="bg-[#0a0a0a] border border-zinc-800" data-testid="active-ruleset-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="active-ruleset-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="min-w-0">
          <div className="overline flex items-center gap-2"><Crosshair size={12} className="text-[#39FF14]" /> Active Ruleset
            {preview && <span data-testid="active-ruleset-preview-badge" className="mono text-[8px] px-1.5 py-0.5 border border-[#FFCC00]/60 text-[#FFCC00] tracking-widest">PREVIEW · your current pick — build to grade</span>}
          </div>
          <div className="mono text-[11px] text-[#39FF14] font-bold truncate mt-0.5" data-testid="active-ruleset-route">{routeLabel}</div>
          {meta && (
            <div className="mono text-[9px] text-zinc-500 mt-0.5">
              {String(meta.contest_type || "").toUpperCase()} · field {Number(meta.field_size || 0).toLocaleString()} · {meta.max_entries}-max
              {sc?.entry_anchor ? ` · entry anchor ${sc.entry_anchor}` : ""}
            </div>
          )}
          {(meta?.selection || showdownRules?.selection) && (() => { const sel = meta?.selection || showdownRules?.selection; return (
            <div className="mono text-[9px] text-[#39FF14]/80 mt-0.5" data-testid="selection-summary">
              Sim-selected {sel.selected} of {sel.candidates} rule-legal candidates (×{sel.overbuild_factor}) · field {Number(sel.field_samples).toLocaleString()} sampled · payout {sel.payout?.source} · portfolio $EV {Number(sel.portfolio_ev).toFixed(2)} vs first-{sel.selected} build order (cap-unconstrained) {Number(sel.candidate_ev_first_n).toFixed(2)}{sel.cap_relaxed ? " · CAP RELAXED to fill" : ""}{sel.soft_cap_lineups ? ` · spine taper: soft cap ${sel.soft_cap_pct}% (${sel.soft_cap_lineups} lineups) → hard cap ${sel.exposure_cap_lineups}` : ""}
            </div>
          ); })()}
          {meta?.ownership_source && (
            <div className={`mono text-[9px] mt-0.5 ${meta.ownership_source === "market" ? "text-zinc-500" : "text-[#FFCC00]"}`} data-testid="ownership-source-badge">
              Ownership: {meta.ownership_source === "market" ? `market feed (${meta.ownership_coverage_pct}% coverage)` : "INTERNAL MODEL — market ownership missing, leverage rules are estimates"}
            </div>
          )}
        </div>
        {open ? <CaretUp size={14} className="text-zinc-500" /> : <CaretDown size={14} className="text-zinc-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-zinc-900 pt-3 space-y-0.5" data-testid="active-ruleset-body">
          {showdownRules ? (
            <>
              <Row label="CPT max exposure" value={`${showdownRules.cpt_max_exposure_pct}%`} testId="rule-cpt-max" />
              <Row label="Any-slot max exposure" value={`${showdownRules.max_exposure_pct ?? 100}%`} />
              {showdownRules.punt_cap_under_1k_pct != null && (
                <Row label="Punt spine cap" value={`< $1k ${showdownRules.punt_cap_under_1k_pct}% · < $3k ${showdownRules.punt_cap_under_3k_pct}%${showdownRules.punt_cap_under_5k_pct != null ? ` · < $5k ${showdownRules.punt_cap_under_5k_pct}%` : ""}`} testId="rule-punt-cap" />
              )}
              {showdownRules.max_cheap_bodies != null && showdownRules.max_cheap_bodies < 6 && (
                <Row label="Max sub-$1k bodies / lineup" value={String(showdownRules.max_cheap_bodies)} testId="rule-max-cheap" />
              )}
              <Row label="Leverage captains" value={pct(showdownRules.cpt_leverage_share)} />
              <Row label="K/DST at CPT cap" value={`${showdownRules.k_dst_cpt_max_exposure_pct}%`} />
              <Row label="Script mix" value={Object.entries(showdownRules.script_mix || {}).map(([k, v]) => `${k} ${pct(v)}`).join(" · ")} />
              <Row label="Leverage λ" value={showdownRules.leverage_lambda} />
              <Row label="Min unique players" value={showdownRules.min_unique_players} />
            </>
          ) : (
            <>
              <Row label="Salary window" value={`$${Number(config.salary_floor || 0).toLocaleString()} – $${Number(config.salary_cap || 0).toLocaleString()}`} testId="rule-salary" />
              {config.salary_jitter_bands?.length > 0 && <Row label="Salary bands" value={fmtBands(config.salary_jitter_bands)} testId="rule-bands" />}
              <Row label="Ownership window" value={`${config.sum_own_min} – ${config.sum_own_max}%`} testId="rule-own" />
              <Row label="Max exposure" value={`${config.max_exposure_pct}%`} testId="rule-exposure" />
              <Row label="Min unique players" value={config.min_unique_players} testId="rule-unique" />
              <Row label="Stack mix (1G / 2G / 3G)" value={`${pct(config.stack_pct_primary_only)} / ${pct(config.stack_pct_primary_secondary)} / ${pct(config.stack_pct_primary_secondary_tertiary)}`} testId="rule-stacks" />
              <Row label="Dupe budget / ceiling z / λ" value={`${config.max_expected_dupes ?? "—"} / ${config.ceiling_z ?? "—"} / ${config.leverage_lambda ?? "—"}`} testId="rule-edge" />
              <Row label="Low-owned pivots" value={`≥${config.min_low_owned} under ${config.low_owned_thresh}%`} />
              <Row label="Max vs your DST" value={config.max_vs_defense ?? "off"} />
              <Row label="Variance" value={config.global_variance} />
              {config.salary_sweet_spot_bonus > 0 && <Row label="Salary sweet-spots" value={`+${Math.round(config.salary_sweet_spot_bonus * 100)}% nudge`} testId="rule-sweet" />}
              {g && (
                <div className="mt-3 pt-2 border-t border-zinc-900" data-testid="build-scorecard">
                  <div className="overline mb-1">Build vs Rules · {g.n} lineups</div>
                  <Grade ok={g.naked === 0 || Number(config.stack_pct_primary_only) > 0} label="Naked QBs" value={`${g.naked}`} testId="grade-naked" />
                  <Grade ok={g.overCap === 0} label="Over salary cap" value={`${g.overCap}`} testId="grade-cap" />
                  {g.inBand != null && <Grade ok={g.inBand === g.n} label="Inside a salary band" value={`${g.inBand}/${g.n}`} testId="grade-bands" />}
                  <Grade ok={g.ownOut === 0} label="Outside ownership window" value={`${g.ownOut}`} testId="grade-own" />
                  <Grade ok={g.expBreach === 0} label="Exposure breaches" value={`${g.expBreach} (worst ${g.worstPct}%)`} testId="grade-exposure" />
                  {g.mu > 0 && <Grade ok={g.pairViol === 0} label={`Pairs sharing > ${9 - g.mu}`} value={`${g.pairViol}`} testId="grade-unique" />}
                  <Grade ok={g.relaxed / g.n <= 0.4} label="Relaxed-rule lineups" value={`${g.relaxed}/${g.n} (${Math.round((100 * g.relaxed) / g.n)}%)`} testId="grade-relaxed" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
